import isEqual from 'react-fast-compare';
import {
  FilterToolbarGroup,
  FilterToolbarItem,
  FilterToolbarItemId,
  useLocationFilter,
} from 'src/components/Filters';
import { EventDateSelectorV2 } from 'src/core/POS/DateRangeSelector/EventDateSelectorV2';
import { PosMultiSelect } from 'src/core/POS/PosMultiSelect';
import { PosEnumMultiSelect } from 'src/core/POS/PosMultiSelect/PosEnumMultiSelect';
import { ContentId } from 'src/utils/constants/contentId';
import {
  COLLECTION_FILTER_OP_TO_CID,
  PARENT_CATEGORY_TO_CID,
  TOP_LEVEL_CATEGORY_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import {
  ApiEventStatus,
  DateTimeRangeWithRelative,
  EventTimeFrameFilter,
  FilterOperationType,
  TopLevelCategory,
} from 'src/WebApiController';

import { EventSelector } from '../../Selectors/EventSelector';
import { PerformerSelector } from '../../Selectors/PerformerSelector';
import { VenueSelector } from '../../Selectors/VenueSelector';
import { useEventStateFilters } from '../useEventStateFilters';

export type CommonEventFilter = {
  eventTimeFrameFilter: EventTimeFrameFilter | null;
  eventDates: DateTimeRangeWithRelative | null;
  countryCodes: string[] | null;
  stateProvinceIds: number[] | null;
  cityIds: number[] | null;
  topLevelCategories: TopLevelCategory[] | null;
  parentCategories: number[] | null;
  eventStatuses: ApiEventStatus[] | null;

  // there are only for the search-views
  eventOrMappingIds: string[] | null;
  performerIds: number[] | null;
  venueIds: number[] | null;

  isSortDescending: boolean | null;

  operations?: {
    eventDatesOp?: FilterOperationType | null;
    countryCodesOp?: FilterOperationType | null;
    stateProvinceIdsOp?: FilterOperationType | null;
    cityIdsOp?: FilterOperationType | null;
    topLevelCategoriesOp?: FilterOperationType | null;
    parentCategoriesOp?: FilterOperationType | null;
    eventStatusesOp?: FilterOperationType | null;
    eventOrMappingIdsOp?: FilterOperationType | null;
    performerIdsOp?: FilterOperationType | null;
    venueIdsOp?: FilterOperationType | null;
  } | null;
};

export type CommonEventFiltersProps<TFilter extends CommonEventFilter> = {
  initialQuery: TFilter;
  query: TFilter;
  setQuery: (query: TFilter) => void;
  showCatalogFilters?: boolean;
  showEventFilter?: boolean;
  showVenueFilter?: boolean;
  showPerformerFilter?: boolean;
  firstRenderOpen?: boolean;
  additionalFilters?: FilterToolbarItem[];
  forceUseCatalogV2?: boolean;
  supportMultiOperation?: boolean;
  isHorizontalLayout?: boolean;
};

export const useCommonEventFilters = <TFilter extends CommonEventFilter>({
  initialQuery,
  query,
  setQuery,
  showCatalogFilters,
  showEventFilter = true,
  showVenueFilter,
  showPerformerFilter,
  additionalFilters,
  firstRenderOpen,
  forceUseCatalogV2,
  supportMultiOperation = false,
  isHorizontalLayout = false,
}: CommonEventFiltersProps<TFilter>): FilterToolbarGroup => {
  const locationFilters = useLocationFilter({
    supportMultiOperation,
    isHorizontalLayout,
  });
  const eventStateFilters = useEventStateFilters({
    supportMultiOperation,
    isHorizontalLayout,
  });

  const hideCatalogFilters = !showCatalogFilters;

  return {
    titleContentId: ContentId.Events,
    items: [
      {
        filterId: 'eventDates' as FilterToolbarItemId,
        labelContentId: ContentId.EventDate,
        filterQueryKeys: [
          'eventDates',
          'eventTimeFrameFilter',
        ] as FilterToolbarItemId[],
        filterItem: (
          <EventDateSelectorV2
            eventTimeFrame={query.eventTimeFrameFilter}
            value={query.eventDates}
            operation={query.operations?.eventDatesOp ?? null}
            enableOperationSelect={supportMultiOperation}
            isHorizontalLayout={isHorizontalLayout}
            onChange={({
              value: dateRange,
              eventTimeFrameFilter,
              operation,
            }) => {
              if (
                eventTimeFrameFilter !== query.eventTimeFrameFilter ||
                !isEqual(dateRange, query.eventDates) ||
                operation !== query.operations?.eventDatesOp
              ) {
                let isSortDescending = query.isSortDescending;
                if (eventTimeFrameFilter !== EventTimeFrameFilter.Future) {
                  // When sorting by past or by all - we do not want to sort by date asc (as that would put the oldest event first)
                  // Changing this to desc
                  isSortDescending = true;
                } else {
                  isSortDescending = initialQuery.isSortDescending;
                }
                setQuery({
                  ...query,
                  eventTimeFrameFilter: eventTimeFrameFilter ?? null,
                  eventDates: dateRange ?? null,
                  isSortDescending,
                  operations: supportMultiOperation
                    ? {
                        ...query.operations,
                        eventDatesOp: operation,
                      }
                    : undefined,
                });
              }
            }}
          />
        ),
      },
      ...(hideCatalogFilters
        ? []
        : [
            ...(showEventFilter
              ? [
                  {
                    filterId: 'eventOrMappingIds' as FilterToolbarItemId,
                    labelContentId: ContentId.Event,
                    filterQueryKeys: [
                      'eventOrMappingIds',
                    ] as FilterToolbarItemId[],
                    filterItem: (
                      <EventSelector
                        firstRenderOpen={firstRenderOpen}
                        values={query.eventOrMappingIds ?? []}
                        eventTimeFrameFilter={query.eventTimeFrameFilter}
                        eventDatesFilter={query.eventDates}
                        operation={
                          query.operations?.eventOrMappingIdsOp ?? null
                        }
                        operationOptionsContent={
                          supportMultiOperation
                            ? COLLECTION_FILTER_OP_TO_CID
                            : undefined
                        }
                        isHorizontalLayout={isHorizontalLayout}
                        placeholderText={ContentId.SearchAndSelectAnEvent}
                        onChange={({ newValues, operation }) => {
                          if (
                            !isEqual(newValues, query.eventOrMappingIds) ||
                            operation !== query.operations?.eventOrMappingIdsOp
                          ) {
                            setQuery({
                              ...query,
                              eventOrMappingIds: newValues ?? null,
                              operations: supportMultiOperation
                                ? {
                                    ...query.operations,
                                    eventOrMappingIdsOp: operation,
                                  }
                                : undefined,
                            });
                          }
                        }}
                        forceUseCatalogV2={forceUseCatalogV2}
                      />
                    ),
                  },
                ]
              : []),
            ...(showVenueFilter
              ? [
                  {
                    filterId: 'venueIds' as FilterToolbarItemId,
                    labelContentId: ContentId.Venue,
                    filterQueryKeys: ['venueIds'] as FilterToolbarItemId[],
                    filterItem: (
                      <VenueSelector
                        firstRenderOpen={firstRenderOpen}
                        triggerProps={{
                          style: { width: '100%' },
                        }}
                        values={
                          query.venueIds?.map((vid) => vid.toString()) ?? []
                        }
                        operation={query.operations?.venueIdsOp ?? null}
                        operationOptionsContent={
                          supportMultiOperation
                            ? COLLECTION_FILTER_OP_TO_CID
                            : undefined
                        }
                        isHorizontalLayout={isHorizontalLayout}
                        placeholderText={ContentId.SearchAndSelectAVenue}
                        onChange={({ newValues, operation }) => {
                          const valuesAsNumber = newValues?.map((v) =>
                            Number(v)
                          );

                          if (
                            !isEqual(valuesAsNumber, query.venueIds) ||
                            operation !== query.operations?.venueIdsOp
                          ) {
                            setQuery({
                              ...query,
                              venueIds: valuesAsNumber ?? null,
                              operations: supportMultiOperation
                                ? {
                                    ...query.operations,
                                    venueIdsOp: operation,
                                  }
                                : undefined,
                            });
                          }
                        }}
                        forceUseCatalogV2={forceUseCatalogV2}
                      />
                    ),
                  },
                ]
              : []),
            ...(showPerformerFilter
              ? [
                  {
                    filterId: 'performerIds' as FilterToolbarItemId,
                    labelContentId: ContentId.Performer,
                    filterQueryKeys: ['performerIds'] as FilterToolbarItemId[],
                    filterItem: (
                      <PerformerSelector
                        firstRenderOpen={firstRenderOpen}
                        triggerProps={{
                          style: { width: '100%' },
                        }}
                        values={
                          query.performerIds?.map((pid) => pid.toString()) ?? []
                        }
                        placeholderText={ContentId.All}
                        operation={query.operations?.performerIdsOp ?? null}
                        operationOptionsContent={
                          supportMultiOperation
                            ? COLLECTION_FILTER_OP_TO_CID
                            : undefined
                        }
                        isHorizontalLayout={isHorizontalLayout}
                        onChange={({ newValues, operation }) => {
                          const valuesAsNumber = newValues?.map((v) =>
                            Number(v)
                          );
                          if (
                            !isEqual(valuesAsNumber, query.performerIds) ||
                            operation !== query.operations?.performerIdsOp
                          ) {
                            setQuery({
                              ...query,
                              performerIds: valuesAsNumber ?? null,
                              operations: supportMultiOperation
                                ? {
                                    ...query.operations,
                                    performerIdsOp: operation,
                                  }
                                : undefined,
                            });
                          }
                        }}
                        forceUseCatalogV2={forceUseCatalogV2}
                      />
                    ),
                  },
                ]
              : []),
          ]),
      ...locationFilters,
      {
        filterId: 'topLevelCategories' as FilterToolbarItemId,
        filterQueryKeys: ['topLevelCategories'] as FilterToolbarItemId[],
        labelContentId: ContentId.Genre,
        filterItem: (
          <PosEnumMultiSelect
            triggerProps={{ style: { width: '100%' } }}
            values={
              // TODO: set up enum type for this prop
              (query.topLevelCategories as TopLevelCategory[]) || []
            }
            placeholderText={ContentId.AllGenres}
            enableEmptySelection
            operation={query.operations?.topLevelCategoriesOp ?? null}
            operationOptionsContent={
              supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
            }
            isHorizontalLayout={isHorizontalLayout}
            onChange={({ newValues: topLevelCategories, operation }) => {
              if (
                !isEqual(topLevelCategories, query.topLevelCategories) ||
                operation !== query.operations?.topLevelCategoriesOp
              ) {
                setQuery({
                  ...query,
                  topLevelCategories: topLevelCategories ?? null,
                  operations: supportMultiOperation
                    ? {
                        ...query.operations,
                        topLevelCategoriesOp: operation,
                      }
                    : undefined,
                });
              }
            }}
            valueOptionsContent={TOP_LEVEL_CATEGORY_TO_CID}
          />
        ),
      },
      {
        filterId: 'parentCategories' as FilterToolbarItemId,
        labelContentId: ContentId.SubGenre,
        filterQueryKeys: ['parentCategories'] as FilterToolbarItemId[],
        filterItem: (
          <PosMultiSelect
            triggerProps={{ style: { width: '100%' } }}
            values={
              query.parentCategories
                ? query.parentCategories.map((c) => String(c))
                : []
            }
            operation={query.operations?.parentCategoriesOp ?? null}
            operationOptionsContent={
              supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
            }
            isHorizontalLayout={isHorizontalLayout}
            onChange={({ newValues, operation }) => {
              setQuery({
                ...query,
                parentCategories:
                  newValues.length > 0
                    ? newValues.map((value) => Number(value))
                    : null,
                operations: supportMultiOperation
                  ? {
                      ...query.operations,
                      parentCategoriesOp: operation,
                    }
                  : undefined,
              });
            }}
            searchable={true}
            valueOptionsContent={PARENT_CATEGORY_TO_CID}
          />
        ),
      },
      ...eventStateFilters,
      ...(additionalFilters ?? []),
    ],
  };
};
